import type {
  EmulatorDeploymentStatus,
  EmulatorProgress,
  EmulatorProgressStep,
  K8SEmulator,
  SnapshotWithRoot,
} from "../../ctrl/hooks";
import type { StatusTone } from "@repo/ui/na";
import { observeRestart } from "./restarts";
import { newerOnLine, snapIdentity } from "./snapshots";

export const BOOT_STEPS = [
  "Scheduling pod",
  "Restoring snapshot",
  "Booting Android",
  "Starting car service",
  "Connecting stream",
] as const;

export type EmulatorStatus =
  | "Running"
  | "Provisioning"
  | "Booting"
  | "Restarting"
  | "Failed"
  | "Deleting"
  | "Unknown";

export const STATUS_TONE: Record<EmulatorStatus, StatusTone> = {
  Running: "success",
  Provisioning: "info",
  Booting: "info",
  Restarting: "warning",
  Failed: "danger",
  Deleting: "muted",
  Unknown: "muted",
};

export const isBooting = (s: EmulatorStatus) =>
  s === "Provisioning" || s === "Booting" || s === "Restarting";

export function normalizeStatus(raw?: string): EmulatorStatus {
  const s = (raw ?? "").toLowerCase();
  if (s.includes("run") || s === "ready") return "Running";
  if (s.includes("restart")) return "Restarting";
  if (s.includes("boot") || s.includes("start")) return "Booting";
  if (s.includes("pend") || s.includes("provision") || s.includes("creat") || s.includes("schedul"))
    return "Provisioning";
  if (s.includes("fail") || s.includes("error") || s.includes("crash")) return "Failed";
  if (s.includes("delet") || s.includes("terminat")) return "Deleting";
  return "Unknown";
}

/** Progress shown for a booting row: steps, active index and percent. */
export type BootView = Pick<
  EmulatorRow,
  "step" | "progress" | "stepLabel" | "steps" | "message" | "warning" | "node"
>;

/** Percent complete; the active step counts as half done. */
export function stepsPercent(steps: EmulatorProgressStep[]): number {
  if (!steps.length) return 0;
  const score = steps.reduce(
    (n, s) => n + (s.state === "done" || s.state === "skipped" ? 1 : s.state === "active" ? 0.5 : 0),
    0,
  );
  return (score / steps.length) * 100;
}

/** Index of the step in flight: active or failed, else the first pending. */
export function activeStepIndex(steps: EmulatorProgressStep[]): number {
  const i = steps.findIndex((s) => s.state === "active" || s.state === "failed");
  if (i >= 0) return i;
  const p = steps.findIndex((s) => s.state === "pending");
  return p >= 0 ? p : Math.max(0, steps.length - 1);
}

/** Synthetic ladder for ctrl versions that don't send `progress`. */
function legacySteps(step: number): EmulatorProgressStep[] {
  return BOOT_STEPS.map((label, i) => ({
    phase: "deploying",
    label,
    state: i < step ? "done" : i === step ? "active" : "pending",
  }));
}

/**
 * Boot view from server progress, or the legacy "(n/5)" parse only when
 * progress is truly absent. A terminal progress on a still-booting row is
 * not special-cased: the row flips on its next poll.
 */
export function bootView(progress: EmulatorProgress | undefined, legacyStep: number): BootView {
  const steps = progress?.steps ?? [];
  if (!progress || !steps.length) {
    return {
      step: legacyStep,
      progress: ((legacyStep + 0.5) / BOOT_STEPS.length) * 100,
      stepLabel: BOOT_STEPS[legacyStep] ?? BOOT_STEPS[0],
      steps: legacySteps(legacyStep),
    };
  }
  const step = activeStepIndex(steps);
  return {
    step,
    progress: stepsPercent(steps),
    stepLabel: steps[step]?.label ?? "",
    steps,
    message: progress.message || undefined,
    warning: progress.warning || undefined,
    node: progress.node || undefined,
  };
}

/** Overlay fresher (streamed) progress onto a row; no-op once it is not booting. */
export function withProgress(row: EmulatorRow, progress: EmulatorProgress | undefined): EmulatorRow {
  if (!progress || !isBooting(row.status)) return row;
  const view = bootView(progress, row.step);
  return view.steps === row.steps ? row : { ...row, ...view };
}

/** Boot step index from the server status message, e.g. "Booting Android (3/5)". */
export function bootStep(message?: string): number {
  if (!message) return 0;
  const m = message.match(/\((\d+)\s*\/\s*(\d+)\)/);
  if (m) return Math.max(0, Number(m[1]) - 1);
  const i = BOOT_STEPS.findIndex((l) => message.toLowerCase().includes(l.toLowerCase()));
  return i < 0 ? 0 : i;
}

/** "priya.raman@kestrel.dev" → "Priya Raman". Human names first, IDs second. */
export function nameFromEmail(email?: string) {
  if (!email) return "—";
  const local = email.split("@")[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/** Compact relative time for the event feed, e.g. "12s ago". */
export function agoLabel(time: string, now = Date.now()): string {
  const t = Date.parse(time);
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`;
}

export function uptime(createdAt?: string, now = Date.now()) {
  const t = createdAt ? Date.parse(createdAt) : NaN;
  if (Number.isNaN(t)) return "—";
  const m = Math.max(0, Math.floor((now - t) / 60_000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** The signed-in viewer; decides which emulators are "mine" or "shared". */
export type EmulatorViewer = { email?: string; groups: string[] };

export type EmulatorRow = {
  id: string;
  short: string;
  name: string;
  status: EmulatorStatus;
  tone: StatusTone;
  /** Index of the active step in `steps`. */
  step: number;
  /** Percent complete, 0-100. */
  progress: number;
  /** Label of the active step, e.g. "Waiting for the new node". */
  stepLabel: string;
  steps: EmulatorProgressStep[];
  /** What the cluster is doing right now. */
  message?: string;
  /** Non-fatal problem the cluster is retrying. */
  warning?: string;
  node?: string;
  reason?: string;
  owner?: string;
  ownerName: string;
  mine: boolean;
  shared: boolean;
  gpu: boolean;
  groups: string[];
  createdAt?: string;
  snapshotName?: string;
  snapshot?: SnapshotWithRoot;
  project?: string;
  projectLeaf?: string;
  ref?: string;
  sha?: string;
  /** Newer deployable snapshots on the same project and ref. */
  newer: SnapshotWithRoot[];
  /** Human build line the newer snapshots share, e.g. "infotainment · main" or "root android-16". */
  lineLabel?: string;
  raw: K8SEmulator;
  deployment?: EmulatorDeploymentStatus;
};

function failureReason(progress: EmulatorProgress | undefined, legacy?: string): string | undefined {
  if (!progress?.message) return legacy;
  return progress.warning ? `${progress.message} ${progress.warning}` : progress.message;
}

export function toEmulatorRow(
  e: K8SEmulator,
  st: EmulatorDeploymentStatus | undefined,
  snapshots: SnapshotWithRoot[] | undefined,
  me: EmulatorViewer,
  /** Streamed progress; fresher than the polled `st.progress`. */
  streamed?: EmulatorProgress,
): EmulatorRow {
  // The list status is live (pod phase, reconciled with failed records).
  // The deployment record's status is written at deploy time and goes
  // stale, so it only wins for states the pod can't express.
  const live = normalizeStatus(e.status);
  const record = normalizeStatus(st?.status);
  let status: EmulatorStatus =
    record === "Failed" || record === "Deleting" || (live === "Unknown" && record !== "Unknown") ? record : live;
  let step = bootStep(st?.status_message);
  // Older ctrl omits progress entirely despite the generated type.
  let serverProgress: EmulatorProgress | undefined = streamed ?? st?.progress;
  if (e.id && observeRestart(e.id, live === "Running") && status !== "Failed" && status !== "Deleting") {
    status = "Restarting";
    // The old pod still reports running until the new one lands; that is not progress.
    if (serverProgress?.terminal && serverProgress.status === "running") serverProgress = undefined;
    step = live === "Running" ? 0 : Math.max(step, 2);
  }
  const boot = bootView(serverProgress, step);
  const snap = snapshots?.find((s) => s.name === st?.snapshot_name);
  const id = snap ? snapIdentity(snap) : undefined;
  const newer = snap && snapshots ? newerOnLine(snap, snapshots) : [];
  const mine = Boolean(me.email && e.owner === me.email);
  const groups = st?.groups ?? e.groups ?? [];
  return {
    id: e.id ?? "",
    short: (e.id ?? "").slice(0, 8),
    name: st?.display_name || (e.id ?? "").slice(0, 8),
    status,
    tone: STATUS_TONE[status],
    ...boot,
    progress: isBooting(status) ? boot.progress : 100,
    reason: status === "Failed" ? failureReason(serverProgress, st?.status_message) : undefined,
    owner: e.owner,
    ownerName: nameFromEmail(e.owner),
    mine,
    shared: !mine && groups.some((g) => me.groups.includes(g)),
    gpu: Boolean(st?.gpu),
    groups,
    createdAt: e.created_at ?? st?.created_at,
    snapshotName: st?.snapshot_name,
    snapshot: snap,
    project: id?.project,
    projectLeaf: id?.projectLeaf,
    ref: id?.ref ?? (id?.root ? "root" : undefined),
    sha: id?.sha,
    lineLabel: id?.lineLabel,
    newer,
    raw: e,
    deployment: st,
  };
}
